import {
  AspectRatio,
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
} from "@chakra-ui/react";
import { nanoid } from 'nanoid';
import { useState } from "react";
import { BuilderList } from "../../FormBuilderTypes";

type InputGeneratorProps = {
  builder: BuilderList;
  handleBuilders: (tabId: string, option: any, builderId: string) => any;
  tabId: string
};

const InputGenerator = ({ builder, tabId, handleBuilders }: InputGeneratorProps) => {

  const handleInputChange = (e: any, isCheck: boolean) => {
    const { value, checked } = e.target;
    const option = builder.options?.find(opt => opt.id === value) || {};
    (checked || !isCheck ) && handleBuilders(tabId, option?.linkedForm?.builders, builder.id);
  };

  switch (builder.type) {
    case "dropdown":
      return (
        <FormControl isRequired={builder.required} mb="2">
          <FormLabel>{builder.instruction}</FormLabel>
          <Select mb={3} placeholder="Select Options" onChange={(e) => handleInputChange(e, false)} >
            {builder.options?.map((res) => (
              <option value={res.id} key={JSON.stringify(res)}>{res.option}</option>
            ))}
          </Select>
        </FormControl>
      );
    case "file":
      if (builder.title === "Government Id") {
        return (
          <FormControl isRequired={builder.required} mb="2">
            <FormLabel>{builder.instruction}</FormLabel>
            <GovernmentId />
          </FormControl>
        );
      }
      return (
        <FormControl isRequired={builder.required} mb="2">
          <FormLabel>{builder.instruction}</FormLabel>
          <ImageUpload />
        </FormControl>
      );
    case "checkbox":
      return (
        <FormControl isRequired={builder.required} mb="2">
          <FormLabel>{builder.instruction}</FormLabel>
          <Stack>
            {builder.options?.map((res) => (
              <Checkbox 
                size="md" 
                colorScheme="blue" 
                value={res.id} 
                onChange={(e) => handleInputChange(e, false)} 
                key={res.id}
              >
                {res.option}
              </Checkbox>
            ))}
          </Stack>
        </FormControl>
      );
    case "radio":
      return (
        <FormControl isRequired={builder.required} mb="2">
          <FormLabel>{builder.instruction}</FormLabel>
          <RadioGroup defaultValue={builder?.options?.[0]?.id || "1"}>
            <Stack>
              {builder.options?.map((res) => (
                <Radio size="md" value={res.id} colorScheme="blue" key={nanoid()} onChange={(e) => handleInputChange(e, true)}>
                  {res.option}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>
      );
    case "card":
      return (
        <>
          <FormLabel>{builder.instruction}</FormLabel>   
          <AddCard />
        </>
      );
    case "select":
      if (builder.title === "Bank Info") {
        return <BankInfo />;
      }
      return (
        <FormControl isRequired={builder.required} mb="2">
          <FormLabel>{builder.instruction}</FormLabel>
          <Select placeholder="Select Options">
            <option value="option1">Abia</option>
            <option value="option2">Adamawa</option>
            <option value="option3">Akwa-Ibom</option>
            <option value="option3">Bayelsa</option>
            <option value="option3">...</option>
          </Select>
        </FormControl>
      );
    default:
      return (
        <FormControl isRequired={builder.required} mb="2">
          <FormLabel>{builder.instruction}</FormLabel>
          <Input type={builder.type} mb={3} />
        </FormControl>
      );
  }
};

export default InputGenerator;

const ImageUpload = () => {
  const [image, setImage] = useState("");
  const handleChange = (e: any) => {
    const { files } = e.target;
    const file = files[0];
    if (file) setImage(URL.createObjectURL(file));
  };
  return (
    <Box>
      <AspectRatio width="64" ratio={1}>
        <Box
          borderColor="gray.300"
          borderStyle="dashed"
          borderWidth="2px"
          rounded="md"
          shadow="sm"
          role="group"
          transition="all 150ms ease-in-out"
          _hover={{
            shadow: "md",
          }}
        >
          <Box position="relative" height="100%" width="100%">
            <Box
              position="absolute"
              top="0"
              left="0"
              height="100%"
              width="100%"
              display="flex"
              flexDirection="column"
            >
              <Stack
                height="100%"
                width="100%"
                display="flex"
                alignItems="center"
                justify="center"
                spacing="4"
              >
                {image ? (
                  <Image boxSize="200px" src={image} alt="preview" />
                ) : (
                  <Stack p="8" textAlign="center" spacing="1">
                    <Heading fontSize="lg" color="gray.700" fontWeight="bold">
                      Drop image or file here
                    </Heading>
                    <Text fontWeight="light">or click to upload</Text>
                  </Stack>
                )}
              </Stack>
            </Box>
            <Input
              type="file"
              height="100%"
              width="100%"
              position="absolute"
              top="0"
              left="0"
              opacity="0"
              aria-hidden="true"
              accept="image/png, image/jpeg,application/pdf"
              onChange={(e) => handleChange(e)}
            />
          </Box>
        </Box>
      </AspectRatio>
    </Box>
  );
};

const BankInfo = () => {
  return (
    <FormControl mb="2">
      <FormLabel>Enter Bank Details</FormLabel>
      <Select placeholder="Select Bank" mb="2">
        <option value="option1">Access Bank</option>
        <option value="option2">Wema Bank</option>
        <option value="option3">First Bank</option>
        <option value="option3">......</option>
      </Select>
      <FormLabel>Account Number</FormLabel>
      <Input type={"number"} />
    </FormControl>
  );
};

const AddCard = () => {
  return (
    <FormControl mb="2">
      <Input placeholder="Card Number" type="number" mb={3} />
      <Flex gap="4">
          <Input placeholder="Card Expiry" type="text" mb={3} />
          <Input placeholder="CVV" type="number" mb={3} />
      </Flex>
    </FormControl>
  );
};

const GovernmentId = () => {
  return (
    <>
      <ImageUpload />
      <Select placeholder="Select Options" mb="2" mt="2">
        <option value="" disabled selected>
          --Select ID Card--
        </option>
        <option value="driver_license"> Driver's Licence</option>
        <option value="voter_card"> Voter's Card</option>
        <option value="international_passport">International Passport</option>
        <option value="national_id"> National ID Card</option>
      </Select>
      <FormLabel>Document Number</FormLabel>
      <Input />
    </>
  );
};
